import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

/**
 * Hover-to-reveal label for icon-only (compact) toolbar buttons.
 *
 * Usage: add `group/reveal` (plus `gap-0` if the button has a base gap) to
 * the trigger, then wrap the text label in a span with these classes. On
 * hover or keyboard focus the label slides open; otherwise only the icon
 * shows. Pair with an aria-label on the trigger for screen readers.
 */
export const HOVER_REVEAL_LABEL_CLASSES =
  'max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,margin,opacity] duration-300 ease-out group-hover/reveal:ml-1.5 group-hover/reveal:max-w-[14rem] group-hover/reveal:opacity-100 group-focus-visible/reveal:ml-1.5 group-focus-visible/reveal:max-w-[14rem] group-focus-visible/reveal:opacity-100'

/** Below this container width detail-header actions collapse to icon-only. */
export const COMPACT_HEADER_WIDTH = 600

/**
 * Tracks whether the referenced container is narrower than `threshold`.
 * Detail panels resize independently of the viewport (split view, inline
 * sheets), so this observes the element itself rather than the window.
 */
export function useCompactHeader(
  ref: RefObject<HTMLElement | null>,
  threshold: number = COMPACT_HEADER_WIDTH,
): boolean {
  const [isCompact, setIsCompact] = useState(false)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? element.clientWidth
      setIsCompact(width < threshold)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, threshold])

  return isCompact
}
